import React, { useMemo } from "react";
import { useState, useEffect } from 'react';
import { Table, Button, Space, Tooltip, Input, Tag, message } from "antd";
import { EditOutlined, DeleteOutlined, FilePdfOutlined } from "@ant-design/icons";
import { FaTrash, FaEdit, FaChalkboardTeacher } from 'react-icons/fa';
import { ToastContainer, toast, Bounce } from 'react-toastify';
import { CloseCircleOutlined, SearchOutlined } from '@ant-design/icons';
import config from '../../config';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import PermissionGuard from "../Guards/PermissionGuard";
import { PERMISSIONS } from "../../constants/permissions";
import ExportExcelButton from "../Commons/ExportExcelButton";

/** @typedef {{ id: string; title: string }} ExamBankSummary */
/** @typedef {{ id: string; title: string; version?: number; allowStudentChangeTime?: boolean; examBanks?: ExamBankSummary[] }} ExamListItem */

const EXAM_BANKS_VISIBLE = 3;

const buildExamsFilter = (filtersFromCaller) => {
    const filters = [];
    if (filtersFromCaller.title) filters.push(`title=*${filtersFromCaller.title}/i`);
    if (filtersFromCaller.tags) filters.push(`tags=*${filtersFromCaller.tags}/i`);
    if (filtersFromCaller.QCode) filters.push(`qCode=${filtersFromCaller.QCode}`);
    return filters.join(",");
};

const ExamsGrid = ({
    onEdit
}) => {
    const [search, setSearch] = useState("");
    const [exams, setExams] = useState([]);
    const navigate = useNavigate();
    const tagColors = ["bg-green-100 text-green-800", "bg-yellow-100 text-yellow-800", "bg-pink-100 text-pink-800"];

    const [selectedRowKeys, setSelectedRowKeys] = useState([]);
    const [loading, setLoading] = useState(false);
    const [pagination, setPagination] = useState({
        current: 1,
        pageSize: 5, 
        total: 0,
    });
    const [searchFilters, setSearchFilters] = useState({
        title: "",
        tags: ""
    });
    const handleSearchChange = (e, field) => {
        const value = e?.target ? e.target.value : e;
        setSearchFilters((prev) => ({ ...prev, [field]: value }));
    };

    const fetchExamsWithFilters = (override = {}) => {
        const effectiveFilters = { ...searchFilters, ...override };
        fetchExams(1, pagination.pageSize, effectiveFilters);
    };

    const fetchExams = async (page = 1, pageSize = 5, filtersFromCaller = searchFilters) => {
        setLoading(true);
        try {
            const requestBody = {
                Page: page,
                PageSize: pageSize,
                Filter: buildExamsFilter(filtersFromCaller),
                OrderBy: "",
            };

            const response = await axios.post(
                `${config.apiBaseUrl}/api/Exam/GetList`,
                requestBody,
                {
                    headers: {
                        "Content-Type": "application/json",
                        HeaderKey: config.HeaderKey,
                    },
                    withCredentials: true,
                }
            );

            const data = response.data.data || {};
            
            const items = data || [];
            const total = response.data.count || 0;
            setExams(items);
            setPagination((prev) => ({ ...prev, total, current: page, pageSize }));
        } catch (err) {
            toast.error(<p dir="rtl" className="font-custom">جستجو با خطا مواجه شد</p>);
            if (err.response?.status === 401) {
                navigate("/");
            }
        } finally {
            setLoading(false);
        }
    };

    const handleEdit = (record) => {
    navigate(`/dashboard/exams/form`, { state: { initialExam: record } });
};

    const handleExamBankClick = async (bank) => {
        const id = bank?.id;
        if (!id) return;

        const navigateToForm = (bankData) => {
            navigate(`/dashboard/ExamBanks/form`, {
                state: { initialBank: bankData },
            });
        };

        try {
            const response = await axios.get(
                `${config.apiBaseUrl}/api/ExamBank/GetExamBank?id=${id}`,
                {
                    headers: {
                        "Content-Type": "application/json",
                        HeaderKey: config.HeaderKey,
                    },
                    withCredentials: true,
                }
            );
            const detail = response.data?.data ?? response.data;
            navigateToForm({
                id: detail?.id ?? detail?.Id ?? id,
                title: detail?.title ?? detail?.Title ?? bank.title,
                qrCode: detail?.qrCode ?? detail?.QrCode ?? "",
                version: detail?.version ?? detail?.Version ?? 0,
                exams: detail?.subExams ?? detail?.exams ?? detail?.Exams ?? [],
                childBanks:
                    detail?.childExamBanks ??
                    detail?.childBanks ??
                    detail?.ChildBanks ??
                    [],
                users: detail?.users ?? detail?.Users ?? [],
            });
        } catch {
            navigateToForm({
                id,
                title: bank.title,
                version: 0,
                exams: [],
                childBanks: [],
                users: [],
            });
        }
    };

    const renderExamBanks = (banks) => {
        const list = Array.isArray(banks) ? banks : [];
        if (list.length === 0) {
            return <span className="text-gray-400">—</span>;
        }

        const visible = list.slice(0, EXAM_BANKS_VISIBLE);
        const overflow = list.slice(EXAM_BANKS_VISIBLE);

        return (
            <div className="flex flex-wrap gap-1 justify-center">
                {visible.map((bank) => (
                    <Tag
                        key={bank.id}
                        color="blue"
                        className="font-custom m-0 max-w-[140px] truncate"
                        title={bank.title}
                        //onClick={() => handleExamBankClick(bank)}
                    >
                        {bank.title}
                    </Tag>
                ))}
                {overflow.length > 0 && (
                    <Tooltip
                        title={
                            <div className="font-custom text-right" dir="rtl">
                                {overflow.map((bank) => (
                                    <div key={bank.id} className="py-0.5">
                                        {bank.title}
                                    </div>
                                ))}
                            </div>
                        }
                    >
                        <Tag className="font-custom cursor-default m-0">
                            +{overflow.length}
                        </Tag>
                    </Tooltip>
                )}
            </div>
        );
    };

    const handleDelete = (id) => {
        toast.warn(
            <div className="text-right font-custom">
                <p>آیا از حذف این آزمون مطمئن هستید؟</p>
                <div className="flex justify-end gap-2 mt-2">
                    <button
                        className="bg-red-500 hover:bg-red-700 text-white px-3 py-1 rounded"
                        onClick={() => confirmDelete(id)}
                    >
                        حذف
                    </button>
                    <button
                        className="bg-gray-500 hover:bg-gray-700 text-white px-3 py-1 rounded"
                        onClick={() => toast.dismiss()}
                    >
                        انصراف
                    </button>
                </div>
            </div>,
            { autoClose: false, icon: false }
        );
    };
    const confirmDelete = async (id) => {
        try {

            const response = await axios.delete(`${config.apiBaseUrl}/api/Exam/Delete?id=${id}`, {
                headers: {
                    "Content-Type": "application/json",
                    "HeaderKey": config.HeaderKey
                },
                withCredentials: true,
            });
            
            if (!response.status || response.status !== 200) {
                toast.error(<p dir="rtl" className="font-custom">حذف با خطا مواجه شد</p>);
            }
            else { 
                toast.success(<p dir="rtl" className="font-custom">حذف با موفقیت انجام شد</p>);
            }
            fetchExams(pagination.current, pagination.pageSize);
        } catch (err) {
            console.error("Delete error:", err);
            toast.error(<p dir="rtl" className="font-custom">حذف با خطا مواجه شد</p>);
        }
    };
    const ClearSuffix = ({ onClick, style }) => (
        <Tooltip title="پاک‌کردن">
            <CloseCircleOutlined
                onClick={onClick}
                style={{ cursor: "pointer", ...style, textAlign: 'left' }}
                role="button"
                aria-label="clear"
                tabIndex={0}
                onKeyDown={(e) => (e.key === "Enter" || e.key === " ") && onClick()}
            />
        </Tooltip>
    );
    useEffect(() => {
        // Fetch initial 500 questions on component mount
        fetchExams(1, 5);
    }, []);
    const columns = [
        {
            title: "عملیات",
            key: "actions",
            fixed: "left",
            width: 120,
            className: "font-custom ",
            align: "center",
            render: (row) => (
                <div className="flex gap-2 justify-center">
                    <PermissionGuard permission={PERMISSIONS.EXAMS_DELETE}>
                        <Button
                            type="link"
                            className="text-red-500"
                            onClick={() => handleDelete(row.id)}
                            icon={<FaTrash size={18} color="#980000" />}
                        />
                    </PermissionGuard>
                    <PermissionGuard permission={PERMISSIONS.EXAMS_EDIT}>
                        <Button
                            type="link"
                            className="text-blue-500"
                            onClick={() => handleEdit(row)}
                            icon={<FaEdit size={18} />}
                        />
                    </PermissionGuard>
                </div>
            ),
        },
        {
            title: "زمان آزمون(دقیقه)",
            dataIndex: "duration",
            className: "font-custom",
            key: "duration",
            align: "center",
            width: 180,
            //sorter: (a, b) => (a.duration || 0) - (b.duration || 0),
            render: (d) => (d != null ? Math.round(d / 60) : "-"),
        },
        {
            title: "تغییر زمان توسط دانشجو",
            dataIndex: "allowStudentChangeTime",
            key: "allowStudentChangeTime",
            align: "center",
            width: 170,
            className: "font-custom",
            render: (value, record) => (
                <Tag color={(value ?? record.AllowStudentChangeTime) ? "green" : "default"}>
                    {(value ?? record.AllowStudentChangeTime) ? "مجاز" : "غیرمجاز"}
                </Tag>
            ),
        },
        {
            title: "تعداد سوالات",
            dataIndex: "examQuestions",
            className: "font-custom",
            align: "center",
            key: "questions",
            width: 110,
            render: (q) => <Tag>{Array.isArray(q) ? q.length : 0}</Tag>,
        },
        {
            title: "تاریخ ایجاد",
            dataIndex: "createDatetime",
            className: "font-custom",
            key: "createDatetime",
            align: "center",
            width: 180,

            render: (text) => text ? new Date(text).toLocaleDateString('fa-IR') : 'N/A',
        },
        {
            title: "تگ ها",
            dataIndex: "tags",
            key: "tags",
            width: 180,
            align: "center",
            className: "font-custom text-center",
            render: (tags) => (
                <div className="flex flex-wrap gap-1 justify-center">
                    {tags?.map((tag, index) => (
                        <span
                            key={index}
                            className={`${tagColors[index % tagColors.length]} text-xs font-semibold px-2 py-1 rounded`}>
                            {tag}
                        </span>
                    ))}
                </div>
            ),
            filterDropdown: ({ setSelectedKeys, selectedKeys, confirm, clearFilters }) => (
                <div style={{ padding: 8 }}>
                    <Input
                        placeholder="جستجوی تگ آزمون"
                        // FIX 1: Change .tag to .tags
                        value={searchFilters.tags}
                        // FIX 2: Change "tag" to "tags"
                        onChange={(e) => handleSearchChange(e, "tags")}
                        onPressEnter={() => fetchExamsWithFilters()}
                        suffix={
                            <ClearSuffix
                                onClick={() => {
                                    // FIX 3: Change tag to tags
                                    fetchExamsWithFilters({ tags: "" });
                                    setSearchFilters((prev) => ({ ...prev, tags: "" }));
                                }}
                                style={{ visibility: searchFilters.tags ? "visible" : "hidden" }}
                            />
                        }
                        style={{ marginBottom: 8, display: "flex", direction: "rtl" }}
                    />
                    <Space>
                        <Button
                            type="primary"
                            onClick={() => fetchExamsWithFilters()}
                            icon={<SearchOutlined size={16} />}
                            size="small"
                            className="font-custom"
                        >
                            جستجو
                        </Button>
                    </Space>
                </div>
            )
        },
        {
            title: "بانک‌های آزمون",
            dataIndex: "examBanks",
            key: "examBanks",
            width: 220,
            align: "center",
            className: "font-custom text-center",
            render: (examBanks) => renderExamBanks(examBanks),
        },
        {
            title: "عنوان",
            className: "font-custom",
            align: "right",
            dataIndex: "title",
            key: "title",
            filterDropdown: ({ setSelectedKeys, selectedKeys, confirm, clearFilters }) => (
                <div style={{ padding: 8 }}>
                    <Input
                        placeholder="جستجوی عنوان"
                        value={searchFilters.title}
                        onChange={(e) => handleSearchChange(e, "title")}
                        onPressEnter={() => fetchExamsWithFilters()}
                        suffix={
                            <ClearSuffix
                                onClick={() => {
                                    fetchExamsWithFilters({ title: "" });
                                    setSearchFilters((prev) => ({ ...prev, title: "" }));
                                }}
                                style={{ visibility: searchFilters.title ? "visible" : "hidden" }}
                            />
                        }
                        style={{ marginBottom: 8, display: "flex", direction: "rtl" }}
                    />
                    <Space>
                        <Button
                            type="primary"
                            onClick={() => fetchExamsWithFilters()}
                            icon={<SearchOutlined size={16} />}
                            size="small"
                            className="font-custom"
                        >
                            جستجو
                        </Button>
                    </Space>
                </div>
            ),
            render: (text) => <span>{text}</span>,
        }
    ];

    const rowSelection = {
        selectedRowKeys,
        onChange: setSelectedRowKeys,
    };

    return (
        <div className="font-custom rtl text-right p-5">
            <h1 className="text-center text-xl mt-5 mb-5">
                لیست آزمون‌ها
            </h1>
            <div className="flex gap-2">
                <PermissionGuard permission={PERMISSIONS.EXAMS_VIEW}>
                    <ExportExcelButton
                        endpoint={`${config.apiBaseUrl}/api/Exam/ExportExcel`}
                        filter={buildExamsFilter(searchFilters)}
                        fileName="exams.xlsx"
                    />
                </PermissionGuard>
                <PermissionGuard permission={PERMISSIONS.EXAMS_CREATE}>
                    <button
                        className="bg-green-700 hover:bg-green-600 text-white font-semibold py-2 px-4 rounded-full shadow-md transition duration-300 mb-5"
                        onClick={() => navigate("form")}
                    >
                        + جدید
                    </button>
                </PermissionGuard>
            </div>
            <div className="w-full shadow-lg">

                <Table
                    columns={columns}
                    dataSource={exams}
                    loading={loading}
                    rowKey={(record) => record.id}
                    scroll={{ x: 1000 }}
                    pagination={{
                        current: pagination.current,
                        pageSize: pagination.pageSize,
                        total: pagination.total,
                        showSizeChanger: true,
                        pageSizeOptions: ['5', '10', '20', '50'],
                        onChange: (page, pageSize) => {
                            setPagination((prev) => ({
                                ...prev,
                                current: page,
                                pageSize: pageSize,
                            }));
                            fetchExams(page, pageSize);
                        },
                        showTotal: (total, range) => `${range[0]}-${range[1]} از ${total}`,
                    }}
                    bordered
                />
            </div>
            <ToastContainer
                position="bottom-center"
                autoClose={5000}
                hideProgressBar={false}
                newestOnTop={false}
                closeOnClick
                rtl={false}
                pauseOnFocusLoss
                draggable
                className={"font-custom text-center rtl"}
                pauseOnHover
                theme="dark"
                transition={Bounce}
            />
        </div>
    );

}



export default ExamsGrid;